import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Upload,
  Link as LinkIcon,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  X
} from 'lucide-react';
import { createDocumentFingerprint } from '../lib/documentFingerprint';

function TosAnalyzerPage() {
  const navigate = useNavigate();
  const [inputMethod, setInputMethod] = useState('paste');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState('');
  const [processingState, setProcessingState] = useState({
    stage: null, // null | 'checking'extracting' | 'analyzing'
    extractionProgress: 0,
    analysisProgress: 0,
    totalPages: 0,
    currentPage: 0
  });

  const handlePDFUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Upload een geldig PDF-bestand.');
      return;
    }

    setFileName(file.name);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('pdf', file);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/scrape`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to upload PDF');

      const data = await response.json();
      setText(data.text);
      await handleAnalyze(data);
    } catch (error) {
      setError('PDF verwerking mislukt.');
      setProcessingState(prev => ({
        ...prev,
        stage: null
      }));
    }
  };

  const simulateAnalysisProgress = (onProgress) => {
    let progress = 0;
    const interval = 500; // Update every 500ms

    // Calculate steps for different phases
    const steps = [
      { until: 20, speed: 0.8 },   // First 20% - faster
      { until: 40, speed: 0.5 },   // 20-40% - medium
      { until: 60, speed: 0.3 },   // 40-60% - slower
      { until: 80, speed: 0.2 },   // 60-80% - even slower
      { until: 90, speed: 0.1 }    // 80-90% - very slow
    ];

    return setInterval(() => {
      let increment = 0.1; // default increment

      // Find the current step and apply its speed
      for (const step of steps) {
        if (progress < step.until) {
          increment = step.speed;
          break;
        }
      }

      progress = Math.min(progress + increment, 90);
      onProgress(progress);
    }, interval);
  };

  const checkForExistingAnalysis = async (fingerprint) => {
    setProcessingState(prev => ({
      ...prev,
      stage: 'checking'
    }));
  
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/fingerprint/check-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fingerprint }),
      });
  
      if (!response.ok) {
        throw new Error('Failed to check for existing analysis');
      }
  
      const result = await response.json();
  
      // If we found a match, show the feedback before redirecting
      if (result.exists && result.id) {
        setProcessingState(prev => ({
          ...prev,
          stage: 'match_found',
          matchId: result.id,
          similarity: Math.round(result.similarity * 100)
        }));
  
        // Give user a moment to see the feedback before redirecting
        await new Promise(resolve => setTimeout(resolve, 2000));
  
        setProcessingState(prev => ({ ...prev, stage: null }));
        return result;
      }
  
      return { exists: false };
  
    } catch (error) {
      console.error('Error checking existing analysis:', error);
      return { exists: false };
    } finally {
      setProcessingState(prev => ({
        ...prev,
        stage: prev.stage === 'checking' ? null : prev.stage
      }));
    }
  };

  const handleAnalyze = async (data) => {
    const { text, source } = data;

    if (!text.trim()) {
      setError('Voer tekst in of upload een bestand om te analyseren.');
      return;
    }

    const fingerprint = createDocumentFingerprint(text);

    try {
      // First check for existing analysis
      const existingAnalysis = await checkForExistingAnalysis(fingerprint);

      if (existingAnalysis.exists && existingAnalysis.id) {
        // Existing analysis found
        setProcessingState(prev => ({ ...prev, stage: null }));
        navigate(`/analyses/${existingAnalysis.id}`);
        return;
      }

      // No existing analysis found - proceed with new analysis
      setProcessingState(prev => ({
        ...prev,
        stage: 'analyzing',
        analysisProgress: 0
      }));

      const progressInterval = simulateAnalysisProgress((progress) => {
        setProcessingState(prev => ({
          ...prev,
          analysisProgress: Math.floor(progress)
        }));
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text,
          fingerprint,
          source
        }),
      });

      clearInterval(progressInterval);

      if (!response.ok) throw new Error('Analysis failed');

      const data = await response.json();

      setProcessingState(prev => ({
        ...prev,
        analysisProgress: 100,
        stage: null
      }));

      await new Promise(resolve => setTimeout(resolve, 500));

      if (data.id) {
        navigate(`/analyses/${data.id}`);
      }

    } catch (error) {
      console.error('Analysis error:', error);
      setError('Analyse mislukt. Probeer het opnieuw.');
      setProcessingState(prev => ({
        ...prev,
        stage: null
      }));
    }
  };

  const handleUrlSubmit = async () => {
    if (!url.trim()) {
      setError('Voer een geldige URL in.');
      return;
    }

    setProcessingState(prev => ({
      ...prev,
      stage: 'extracting',
      extractionProgress: 0
    }));
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) throw new Error('Failed to fetch URL content');

      const data = await response.json();
      setText(data.text);
      setProcessingState(prev => ({
        ...prev,
        extractionProgress: 100
      }));
      await handleAnalyze(data.text);
    } catch (error) {
      setError('URL-inhoud ophalen mislukt. Controleer de URL en probeer het opnieuw.');
      setProcessingState(prev => ({
        ...prev,
        stage: null
      }));
    }
  };

  const handleReset = () => {
    setText('');
    setUrl('');
    setError(null);
    setFileName('');
    setProcessingState({
      stage: null,
      extractionProgress: 0,
      analysisProgress: 0,
      totalPages: 0,
      currentPage: 0
    });
  };

  const renderProgressIndicator = () => {
    if (!processingState.stage) return null;

    const isChecking = processingState.stage === 'checking';
    const isExtracting = processingState.stage === 'extracting';
    const isAnalyzing = processingState.stage === 'analyzing';
    const isMatchFound = processingState.stage === 'match_found';


    return (
      <div className="space-y-4 mt-4">
        {isExtracting && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Tekst extraheren</span>
              <span>{processingState.extractionProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-300 ease-out"
                style={{ width: `${processingState.extractionProgress}%` }}
              />
            </div>
            {processingState.totalPages > 0 && (
              <p className="text-xs text-gray-500 text-center">
                Pagina {processingState.currentPage} van {processingState.totalPages}
              </p>
            )}
          </div>
        )}


        {isChecking && (
          <div className="flex items-center justify-center space-x-2 text-gray-600">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Controleren op bestaande analyse...</span>
          </div>
        )}

        {isMatchFound && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="w-6 h-6 text-green-500" />
              <div>
                <h4 className="text-sm font-medium text-green-800">
                  Vergelijkbaar Document Gevonden!
                </h4>
                <p className="text-sm text-green-600">
                  {processingState.similarity}% overeenkomst gevonden - doorverwijzen naar bestaande analyse...
                </p>
              </div>
            </div>
          </div>
        )}


        {isAnalyzing && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>AI Analyse</span>
              <span>{processingState.analysisProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div
                className="h-full bg-green-600 transition-all duration-300 ease-out"
                style={{ width: `${processingState.analysisProgress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 text-center">
              Verwerken met AI
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-nn-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">


          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-nn-500 mb-6">Voeg een uitspraak toe</h1>
            <p className="text-nn-gray-600">
              Upload een PDF, plak tekst of voer een URL in voor analyse
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm">
            <div className="border-b border-nn-gray-200">
              <nav className="flex divide-x divide-nn-gray-200">
                {[
                  { id: 'paste', label: 'Tekst Plakken' },
                  { id: 'upload', label: 'PDF Uploaden' },
                  { id: 'url', label: 'URL Invoeren' }
                ].map((method) => (
                  <button
                    key={method.id}
                    onClick={() => { handleReset(); setInputMethod(method.id); }}
                    className={`flex-1 px-4 py-4 text-sm font-medium ${inputMethod === method.id
                      ? 'text-nn-500 bg-nn-50'
                      : 'text-nn-gray-500 hover:text-nn-gray-700 hover:bg-nn-gray-50'
                      }`}
                    disabled={processingState.stage !== null}
                  >
                    {method.id === 'paste' && <FileText className="w-5 h-5 mx-auto mb-1" />}
                    {method.id === 'upload' && <Upload className="w-5 h-5 mx-auto mb-1" />}
                    {method.id === 'url' && <LinkIcon className="w-5 h-5 mx-auto mb-1" />}
                    {method.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {inputMethod === 'paste' && (
                <div className="space-y-4">
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Plak hier je documenttekst..."
                    className="w-full h-64 p-4 border border-nn-gray-200 rounded-lg focus:ring-2 focus:ring-nn-500 focus:border-nn-500"
                    disabled={processingState.stage !== null}
                  />
                  <button
                    onClick={() => handleAnalyze()}
                    disabled={!text.trim() || processingState.stage !== null}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-nn-500 text-white rounded-lg hover:bg-nn-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingState.stage === 'analyzing' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Analyseren...
                      </>
                    ) : (
                      <>
                        Document Analyseren
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              )}

              {inputMethod === 'upload' && (
                <div className="space-y-4">
                  <div className="flex flex-col items-center justify-center border-2 border-dashed border-nn-gray-300 rounded-lg p-6">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handlePDFUpload}
                      className="hidden"
                      id="file-upload"
                      disabled={processingState.stage !== null}
                    />
                    {!fileName ? (
                      <label
                        htmlFor="file-upload"
                        className="cursor-pointer flex flex-col items-center space-y-2"
                      >
                        <Upload className="w-12 h-12 text-nn-gray-400" />
                        <span className="text-sm text-gray-500">Klik om te uploaden of sleep een bestand</span>
                        <span className="text-xs text-gray-400">Alleen PDF-bestanden</span>
                      </label>
                    ) : (
                      <div className="w-full">
                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <FileText className="w-5 h-5 text-blue-600" />
                            <span className="text-sm font-medium text-gray-700">{fileName}</span>
                          </div>
                          {processingState.stage === null && (
                            <button
                              onClick={handleReset}
                              className="text-gray-400 hover:text-gray-500"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {renderProgressIndicator()}
                </div>
              )}

              {inputMethod === 'url' && (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="Voer document URL in..."
                      className="flex-1 p-2 border border-nn-gray-200 rounded-lg focus:ring-2 focus:ring-nn-500 focus:border-nn-500"
                      disabled={processingState.stage !== null}
                    />
                    <button
                      onClick={handleUrlSubmit}
                      disabled={!url.trim() || processingState.stage !== null}
                      className="px-4 py-2 bg-nn-500 text-white rounded-lg hover:bg-nn-600 disabled:opacity-50 flex items-center gap-2"
                    >
                      {processingState.stage === 'extracting' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        'Ophalen'
                      )}
                    </button>
                  </div>
                  {renderProgressIndicator()}
                </div>
              )}

              {error && (
                <div className="mt-4 p-4 bg-red-50 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default TosAnalyzerPage;